import { memo } from "react";
import {
  TableBody,
  TableRow,
  TableHead,
  Table,
  TableContainer,
  styled,
  TableCell,
  Box,
  IconButton
} from "@mui/material";
import { IconCopy, IconTrash } from "@tabler/icons-react";
import { useFormContext, useWatch } from "react-hook-form";
import MxFormL3InputField from "@rf-l3/form/MxFormL3InputField";
import MxFormL3TextThousandField from "@rf-l3/form/MxFormL3TextThousandField";
import MxFormL3TextDateField from "@rf-l3/form/MxFormL3DatePickerField";
import MxCheck from "@rf-l1/form/MxCheck";
import useAlert from "@rf-hooks/useAlert";
import {
  CPropertyBuildingFormKeys,
  IPropertyBuildingForm
} from "@rf/property/module/components/property-form/util/schemas/building-form/building.schema";
import { CPropertyFormKeys } from "@rf/property/module/components/property-form/util/schemas/property-schema/propertySchema";
import { IMxCatchmentTable } from "../model/catchment.interface";
import MxTypography from "@rf-l1/MxTypography";

/**
 * Tabla para gestionar las etapas/edificios del proyecto
 * Permite editar, duplicar y eliminar elementos
 */
export const MxCatchmentTable = ({
  buildings,
  duplicateItem,
  removeItem,
  maxHeight = 330,
  responsive = true
}: IMxCatchmentTable) => {
  const COL_WIDTHS = {
    name: { xs: 180, sm: 250, md: 300, },
    units: { xs: 100, sm: 120, md: 100 },
    delivery: { xs: 120, sm: 130, md: 160 },
    actions: { xs: 70, sm: 100, md: 190 }
  } as const;

  return (
    <Box sx={{ width: "100%" }}>
      <StyledTableWrapper responsive={responsive}>
        <ResponsiveScrollableContainer maxHeight={maxHeight}>
          <StyledTable size="small" aria-label="catchment table" stickyHeader>
            <TableHead>
              <TableRow>
                <StyledTableCell align="left" sx={{ minWidth: COL_WIDTHS.name }}>
                  Nombre de la etapa
                </StyledTableCell>
                <StyledTableCell align="left" sx={{ minWidth: COL_WIDTHS.units }}>
                  <Box sx={{ display: { xs: "none", sm: "block" } }}>
                    Cant. de unidades de la etapa
                  </Box>
                  <Box sx={{ display: { xs: "block", sm: "none" } }}>Unidades</Box>
                </StyledTableCell>
                <StyledTableCell align="left" sx={{ minWidth: COL_WIDTHS.delivery }}>
                  <Box sx={{ display: { xs: "none", sm: "block" } }}>
                    Fec. de entrega de la etapa
                  </Box>
                  <Box sx={{ display: { xs: "block", sm: "none" } }}>Entrega</Box>
                </StyledTableCell>
                <StyledTableCell align="left" sx={{ minWidth: COL_WIDTHS.actions }}>
                  <Box sx={{ display: { xs: "none", md: "block" } }}>
                    Cotejar si tiene la etapa captada
                  </Box>
                  <Box sx={{ display: { xs: "block", md: "none" } }}>Captada</Box>
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {buildings.map((row, index) => (
                <TableRow key={row.uuid}>
                  <TableCell component="th" scope="row" sx={{ minWidth: COL_WIDTHS.name }}>
                    <MxFormL3InputField
                      type="text"
                      withreacthookform={1}
                      name={`${CPropertyFormKeys.building}.${index}.${CPropertyBuildingFormKeys.name}`}
                    />
                  </TableCell>
                  <TableCell align="left" sx={{ minWidth: COL_WIDTHS.units }}>
                    <MxFormL3TextThousandField
                      withreacthookform={1}
                      name={`${CPropertyFormKeys.building}.${index}.${CPropertyBuildingFormKeys.unitsQuantity}`}
                    />
                  </TableCell>
                  <TableCell align="left" sx={{ minWidth: COL_WIDTHS.delivery }}>
                    <MxFormL3TextDateField
                      withreacthookform={1}
                      name={`${CPropertyFormKeys.building}.${index}.${CPropertyBuildingFormKeys.dateDelivery}`}
                    />
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      minWidth: COL_WIDTHS.actions,
                      gap: { xs: 0.5, sm: 1 },
                      alignItems: "center",
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: 'minmax(auto,120px) auto 24px 24px',
                        sm: 'minmax(auto,120px) 25px 25px 25px'
                      }
                    }}
                  >

                    <MxTypography color="grey.700" variant="overline" >
                      ¿Esta captada?
                    </MxTypography>
                    <MxCatchmentField index={index} />
                    <StyledDuplicateButton
                      aria-label="Duplicar"
                      title="Duplicar"
                      onClick={() => duplicateItem(index)}
                      size="small"
                    >
                      <IconCopy size={16} />
                    </StyledDuplicateButton>
                    {buildings.length > 1 && (
                      <StyledDeleteButton
                        aria-label="Eliminar"
                        title="Eliminar"
                        onClick={() => removeItem(index)}
                        size="small"
                      >
                        <IconTrash size={16} />
                      </StyledDeleteButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </StyledTable>
        </ResponsiveScrollableContainer>
      </StyledTableWrapper>
    </Box>
  );
};

const MxCatchmentField = memo(({ index }: { index: number }) => {
  const watch = useWatch({
    name: `${CPropertyFormKeys.building}.${index}.${CPropertyBuildingFormKeys.isCaptured}`,
    defaultValue: false
  });
  const { warningAlert } = useAlert();
  const { setValue } = useFormContext();
  const buildings = useWatch({
    name: CPropertyFormKeys.building,
    defaultValue: []
  });
  const onCapturedChange = () => {
    if (
      buildings.filter((b: IPropertyBuildingForm) => b.isCaptured).length === 1 &&
      watch === true
    ) {
      warningAlert({
        title: "Aviso",
        message: "Debe tener al menos un edificio captado",
        confirmBtnMessage: "Aceptar"
      });
      return;
    }
    setValue(
      `${CPropertyFormKeys.building}.${index}.${CPropertyBuildingFormKeys.isCaptured}`,
      !watch
    );
  };

  return (
    <MxCheck
      name={`${CPropertyFormKeys.building}.${index}.${CPropertyBuildingFormKeys.isCaptured}`}
      value={watch}
      onChange={onCapturedChange}
    />
  );
});

export default MxCatchmentTable;

// Styled Components
const StyledTable = styled(Table)(({ theme }) => ({
  tableLayout: "fixed",
  width: "100%"
}));

const StyledTableWrapper = styled(Box)<{ responsive?: boolean }>(({ theme, responsive }) => ({
  overflow: "hidden",
  borderRadius: theme.spacing(1),
  ...(responsive && {
    [theme.breakpoints.down("lg")]: {
      overflowX: "auto",
      "& .MuiTable-root": { minWidth: 800 }
    },
    [theme.breakpoints.down("md")]: {
      overflowX: "auto",
      "& .MuiTable-root": { minWidth: 700 }
    },
    [theme.breakpoints.down("sm")]: {
      overflowX: "auto",
      "& .MuiTable-root": { minWidth: 600 }
    }
  })
}));

const StyledDeleteButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: "rgba(212, 64, 69, 0.568)",
  padding: theme.spacing(0.5),
  minWidth: "auto",

  "& svg": {
    color: "white",
    height: "1rem",
    width: "1rem"
  },

  "&:hover": {
    backgroundColor: "rgba(212, 64, 69, 0.7)",
    transform: "scale(1.1)",
    transition: "0.3s"
  },

  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0.3),
    "& svg": {
      height: "0.875rem",
      width: "0.875rem"
    }
  }
}));

const StyledDuplicateButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: "rgba(64, 106, 212, 0.568)",
  padding: theme.spacing(0.5),
  minWidth: "auto",

  "& svg": {
    color: "white",
    height: "1rem",
    width: "1rem"
  },

  "&:hover": {
    backgroundColor: "rgba(64, 106, 212, 0.7)",
    transform: "scale(1.1)",
    transition: "0.3s"
  },

  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0.3),
    "& svg": {
      height: "0.875rem",
      width: "0.875rem"
    }
  }
}));

const ResponsiveScrollableContainer = styled(TableContainer)<{ maxHeight?: number }>(({ theme, maxHeight = 330 }) => ({
  maxHeight,
  overflow: "hidden",
  overflowY: "auto",
  "& .MuiTableHead-root": {
    backgroundColor: theme.palette.background.paper
  },
  [theme.breakpoints.down("md")]: {
    maxHeight: maxHeight * 0.85
  },
  [theme.breakpoints.down("sm")]: {
    maxHeight: maxHeight * 0.76
  },
  "&::-webkit-scrollbar": {
    width: 8,
    height: 8
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: theme.palette.grey[100],
    borderRadius: 4
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: theme.palette.grey[400],
    borderRadius: 4,
    "&:hover": {
      backgroundColor: theme.palette.grey[500]
    }
  }
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 800,
  padding: theme.spacing(1, 2),
  color: theme.palette.primary.main,
  fontSize: theme.typography.pxToRem(11) + " !important",

  // Responsive font size
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.75rem",
    padding: theme.spacing(0.5, 1)
  }
}));
